import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { Theme } from '@mui/material';
import type { IdentityRequirementGroup } from '../IdentityRequirementSummary/IdentityRequirementSummary';

/** Board slot 04 (approved copy, sole consumer): the substitution note shown under the
 *  two-document requirement card when a non-Australian-resident customer needs an alternative to
 *  the Medicare card. */
const MEDICARE_SUBSTITUTION_NOTE =
  'If you are not an Australian resident, another government issued ID can replace the Medicare card.';

/** Board slot 05 (approved copy, sole consumer): the desktop webcam requirement's own label.
 *  Slot 05 carried two board wordings (mobile and desktop), but the board's stylesheet hides the
 *  camera requirement card on mobile (`.device .req .reqcard:last-of-type { display: none }`), so
 *  the mobile wording never paints on the board or in anything built from it. Only the desktop
 *  wording ships. */
const CAMERA_REQUIREMENT = 'A laptop or desktop with a working webcam, for the face check.';

/**
 * Maps WorkflowMetadata's identityDocumentCount to IdentityRequirementSummary's groups prop.
 * `documentCount` is the ONLY field this reads: `documentTypeChoices` is deliberately never
 * consumed here, because at count 2 it discloses whether the individual has an Australian
 * address, on an endpoint gated only by the tracking reference (the backend field's own
 * description now states the same constraint independently).
 *
 * Returns undefined for 0, undefined or null: "the field comes back null and the panel does not
 * render." The caller passes the result straight through to
 * MagicLinkContinue's `requirements` slot, which already handles an undefined value by omitting
 * the requirements block entirely.
 */
export function buildIdentityRequirementGroups(
  documentCount: number | null | undefined,
): ReadonlyArray<IdentityRequirementGroup> | undefined {
  if (!documentCount) {
    return undefined;
  }

  // The identity document lists (Driver's licence, Passport, Medicare card) and the 1 or 2
  // document rule are copied in verifime-email-templates, in the `oneDocument` and `twoDocuments`
  // blocks of templates/customer-invitation/invitation-email.mjml and invitation-email.json.
  // Change both together.
  const documentItems = [
    { key: 'drivers-licence', label: "Driver's licence" },
    { key: 'passport', label: 'Passport' },
    ...(documentCount >= 2 ? [{ key: 'medicare-card', label: 'Medicare card' }] : []),
  ];

  // Ruling 9: this used to also return a "camera" group (hiddenOnMobile: true, generic
  // "phone or laptop" wording, now deleted). C3 hides that card on every mobile width,
  // and no task in this plan ever enabled it on desktop either, so it rendered under no
  // condition at all: a marked placeholder Charles could never actually approve. Deleted rather
  // than kept as an accepted-but-unrendered export. Task 16 builds its own desktop-specific
  // camera group separately (buildDesktopCameraGroup), rendered through
  // MagicLinkContinue's own desktopRequirements slot, never through this function's array.
  return [
    {
      key: 'documents',
      label: 'Government issued ID',
      rule: documentCount >= 2 ? 'Any 2 of these 3' : 'Any 1 of these 2',
      items: documentItems,
      note: documentCount >= 2 ? MEDICARE_SUBSTITUTION_NOTE : undefined,
    },
  ];
}

/**
 * The desktop-only webcam requirement card. Rendered exclusively through MagicLinkContinue's
 * `desktopRequirements` slot, which is itself hidden below the theme's `sm` breakpoint, so this
 * group carries no `hiddenOnMobile` of its own: gating it a second time, per group, would only
 * duplicate that rule and risk the two disagreeing later. Never rendered through
 * `buildIdentityRequirementGroups` above, whose "documents" group is the only thing
 * `requirements` (the mobile slot) ever shows.
 */
export function buildDesktopCameraGroup(): IdentityRequirementGroup {
  return {
    key: 'camera',
    label: 'A webcam',
    items: [
      {
        key: 'camera',
        label: CAMERA_REQUIREMENT,
        // Without this, IdentityRequirementSummary falls back to its default DescriptionIcon (a
        // document glyph), which is wrong for a webcam requirement. Styled to match that
        // fallback's own colour and 20px icon box (see IdentityRequirementSummary.tsx's own
        // comment on `fontSize` for why it is `'20px'` rather than a token, including the
        // measurement that ruled out `8xs`) so only the glyph
        // differs. The two sites are deliberately coupled: both draw the same 20px box, and
        // moving one without the other reintroduces the row-height mismatch this value fixes.
        icon: (
          <PhotoCameraIcon
            aria-hidden
            sx={(theme: Theme) => ({
              color: theme.palette.primary.main,
              fontSize: '20px',
            })}
          />
        ),
      },
    ],
  };
}
